import { Helmet } from "react-helmet-async";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeader";

function RecoveryRecords() {
  const navigate = useNavigate();
  const [recoveries, setRecoveries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [companyLanguage, setCompanyLanguage] = useState('ur');

  useEffect(() => {
    fetchCompanyData();
    fetchRecoveries();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const companyData = response.data.company;
      setCompany(companyData);

      const dashboardType = companyData?.dashboardType || companyData?.dashboard_type || 'main';
      const isPart2 = dashboardType === 'part2' || dashboardType === 'dashboard-part2' || dashboardType === 'part-2' || dashboardType === 'ur' || dashboardType === 'urdu';

      setCompanyLanguage(isPart2 ? 'ur' : (companyData?.language || 'ur'));
    } catch (error) {
      console.error("Fetch company error:", error);
    }
  };

  const fetchRecoveries = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get("/recovery/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recoveriesData = response.data.recoveries || [];
      setRecoveries(recoveriesData);
    } catch (error) {
      console.error("Fetch recoveries error:", error);
      toast.error(companyLanguage === 'ur' ? 'ریکوریز لوڈ کرنے میں ناکام' : "Failed to load recoveries");
    } finally {
      setIsLoading(false);
    }
  };

  // Group recoveries by date and calculate totals
  const recoveryByDate = useMemo(() => {
    const grouped = {};

    recoveries.forEach(recovery => {
      const dateKey = new Date(recovery.date).toLocaleDateString('en-CA'); // YYYY-MM-DD format (local)

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          count: 0,
          totalAmount: 0
        };
      }

      grouped[dateKey].count += 1;
      grouped[dateKey].totalAmount += recovery.recoveryAmount || 0;
    });

    // Convert to array and sort by date (newest first)
    return Object.values(grouped).sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });
  }, [recoveries]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(companyLanguage === 'ur' ? 'ur-PK' : 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
          <p className={`mt-4 text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
            {companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{companyLanguage === 'ur' ? 'ریکوری ریکارڈ' : 'Recovery Records'}</title>
      </Helmet>
      <div className={`min-h-screen bg-brand-background ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
        {/* Company Header Hidden */}
        {/* <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage}
          onLogout={() => {
            localStorage.removeItem("companyToken");
            localStorage.removeItem("companyData");
            navigate("/company/login");
          }}
          bills={[]}
          sales={[]}
          recoveries={recoveries}
          persons={[]}
        /> */}

        <div className="container mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-white via-green-50/30 to-white rounded-xl shadow-xl p-6 mb-6 border-2 border-green-200 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, #059669 1px, transparent 0)`,
                backgroundSize: '30px 30px'
              }}></div>
            </div>

            <div className="relative z-10 flex justify-between items-center flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {/* Back Button */}
                <button
                  onClick={() => {
                    const dashboardType = company?.dashboardType || company?.dashboard_type || 'main';
                    const isPart2 = dashboardType === 'part2' || dashboardType === 'dashboard-part2' || dashboardType === 'part-2' || dashboardType === 'ur' || dashboardType === 'urdu';
                    navigate(isPart2 ? '/company/dashboard-part2?tab=recoveries' : '/company/dashboard?tab=recoveries');
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 shadow-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {companyLanguage === 'ur' ? 'واپس' : 'Back'}
                </button>

                <div>
                  <h1 className={`text-3xl font-bold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                    {companyLanguage === 'ur' ? 'ریکوری ریکارڈ' : 'Recovery Records'}
                  </h1>
                  <p className={`text-sm text-gray-600 mt-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                    {companyLanguage === 'ur' ? 'تاریخ کے لحاظ سے ریکوریز کا خلاصہ' : 'Recovery summary by date'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  const langParam = companyLanguage === 'ur' ? '' : '?lang=en';
                  navigate(`/recovery/create${langParam}`);
                }}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {companyLanguage === 'ur' ? 'نیا ریکوری' : 'New Recovery'}
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          {recoveryByDate.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-green-100 text-sm mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      {companyLanguage === 'ur' ? 'کل ریکوریز' : 'Total Recoveries'}
                    </p>
                    <p className="text-3xl font-bold">
                      {recoveryByDate.reduce((sum, item) => sum + item.count, 0)}
                    </p>
                  </div>
                  <div className="bg-white/20 rounded-full p-3">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-emerald-100 text-sm mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                    </p>
                    <p className="text-3xl font-bold">
                      Rs. {recoveryByDate.reduce((sum, item) => sum + item.totalAmount, 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white/20 rounded-full p-3">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-teal-100 text-sm mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      {companyLanguage === 'ur' ? 'کل دن' : 'Total Days'}
                    </p>
                    <p className="text-3xl font-bold">
                      {recoveryByDate.length}
                    </p>
                  </div>
                  <div className="bg-white/20 rounded-full p-3">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table Section */}
          {recoveryByDate.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center border-2 border-gray-200">
              <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className={`text-xl text-gray-600 mb-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur'
                  ? 'ابھی کوئی ریکوری نہیں'
                  : 'No recoveries found'}
              </p>
              <p className={`text-sm text-gray-500 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur'
                  ? 'پہلی ریکوری بنانے کے لیے اوپر بٹن دبائیں'
                  : 'Click the button above to create your first recovery'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-xl overflow-hidden border-2 border-green-200">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 text-white">
                    <tr>
                      <th className={`px-6 py-4 text-left font-bold text-lg ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                        </div>
                      </th>
                      <th className={`px-6 py-4 text-center font-bold text-lg ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {companyLanguage === 'ur' ? 'کل ریکوریز' : 'Total Recoveries'}
                        </div>
                      </th>
                      <th className={`px-6 py-4 text-right font-bold text-lg ${companyLanguage === 'ur' ? 'font-urdu text-left' : ''}`}>
                        <div className="flex items-center justify-end gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recoveryByDate.map((item, index) => (
                      <tr
                        key={item.date}
                        className={`border-b border-gray-200 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                      >
                        <td className={`px-6 py-4 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="font-semibold text-gray-900">
                              {formatDate(item.date)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 bg-green-100 text-green-800 font-bold text-lg rounded-full">
                            {item.count}
                          </span>
                        </td>
                        <td className={`px-6 py-4 ${companyLanguage === 'ur' ? 'font-urdu text-left' : 'text-right'}`}>
                          <span className="font-bold text-emerald-700 text-lg">
                            Rs. {item.totalAmount.toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gradient-to-r from-green-700 via-emerald-700 to-green-700 text-white">
                    <tr>
                      <td className={`px-6 py-4 font-bold text-lg ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          {companyLanguage === 'ur' ? 'کل' : 'Total'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center font-bold text-lg">
                        <span className="inline-flex items-center justify-center px-3 py-1 bg-white/20 text-white rounded-full">
                          {recoveryByDate.reduce((sum, item) => sum + item.count, 0)}
                        </span>
                      </td>
                      <td className={`px-6 py-4 font-bold text-lg ${companyLanguage === 'ur' ? 'font-urdu text-left' : 'text-right'}`}>
                        Rs. {recoveryByDate.reduce((sum, item) => sum + item.totalAmount, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default RecoveryRecords;
